'use client';

import React, { Suspense } from 'react';
import { Group, Badge, Container, Text, Loader } from '@mantine/core';
import { AnimatePresence, motion } from 'framer-motion';
import MentorGrid from '@/components/MentorGrid';
import MentorSearchHero from '@/components/mentorSearch/mentorSearchHero';
import AdvancedFiltersModal from '@/components/mentorSearch/advancedFiltersModal';
import { useMentorSearchStore } from '@/stores/useMentorSearchStore';
import FiltersSummary from '@/components/mentorSearch/filtersSummary';
import Section from '@/components/ui/Section';
import { useSearchParams } from 'next/navigation';
import { AnimatedGradientBackground } from '@/components/AnimatedGradientBackground';

// Component that handles URL search params
function MentorsSearchParamsHandler() {
  const searchParams = useSearchParams();
  const { setFilters, setModalValues, fetchMentors, users, hasSearched } = useMentorSearchStore();

  // Initialize filters from URL parameters
  React.useEffect(() => {
    const expertiseParam = searchParams.get('expertise');
    const nameParam = searchParams.get('name');
    const locationParam = searchParams.get('location');
    const badgeParam = searchParams.get('badge');
    
    const urlFilters: any = {};
    
    if (expertiseParam) {
      urlFilters.expertise = expertiseParam.split(',');
    }
    if (nameParam) {
      urlFilters.name = nameParam;
    }
    if (locationParam) {
      urlFilters.location = locationParam;
    }
    if (badgeParam) {
      urlFilters.badge = badgeParam.split(',');
    }
    
    // Only apply filters if we have any from the URL AND we don't already have results
    // This prevents refetching when navigating back from a profile
    if (Object.keys(urlFilters).length > 0 && users.length === 0) {
      setFilters(urlFilters);
      setModalValues(urlFilters);
      fetchMentors(true, urlFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only run when URL params change, not when store functions change

  return null;
}

export default function MentorsPage() {
  const { filters, users, isLoading } = useMentorSearchStore();

  // Clear navigation flag on mount
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('fromMentorSearch');
    }
  }, []);

  // Restore scroll position after content loads
  React.useEffect(() => {
    if (typeof window !== 'undefined' && !isLoading && users && users.length > 0) {
      const savedScrollY = sessionStorage.getItem('mentorSearchScrollY');
      
      if (savedScrollY) {
        const scrollY = parseInt(savedScrollY, 10);
        // Use requestAnimationFrame to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo({ top: scrollY, behavior: 'instant' });
            sessionStorage.removeItem('mentorSearchScrollY');
          });
        });
      }
    }
  }, [isLoading, users]);

  // Create a stable key based on search state to prevent flicker
  const searchKey = React.useMemo(() => {
    const filterKey = JSON.stringify(filters);
    const userCount = users?.length || 0;
    return `${filterKey}-${userCount}`;
  }, [filters, users?.length]);

  return (
    <AnimatedGradientBackground isFullPage>
      <Section py="xl" contentWidth="lg" fullHeight>
        <Suspense fallback={null}>
          <MentorsSearchParamsHandler />
        </Suspense>
        <MentorSearchHero />
        <FiltersSummary filters={filters} />

        <div className="mt-10" style={{ marginTop: 30 }}>
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Group justify="center" mt="xl">
                  <Loader size="lg" />
                </Group>
              </motion.div>
            ) : (
              <motion.div
                key={searchKey}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.3 }}
              >
                <MentorGrid users={users} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <AdvancedFiltersModal />
      </Section>
    </AnimatedGradientBackground>
  );
}
